import { useMemo } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiDatabase2Line, RiCheckLine, RiDownloadLine, RiFileCopyLine } from '@remixicon/react';
import {
  Button,
  CopyButton,
  CopyableValue,
  Flex,
  InfraCodeBlock,
  InfraDivider,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagValue,
  InfraTagRow,
  Text,
} from '../ui/InfraPrimitives';

export const SQLServerCDCInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const templateType = annotations['infrastructure.backstage.io/type'] || '';
  const sqlserverHost = annotations['sqlserver.infrastructure/host'];
  const sqlserverPort = annotations['sqlserver.infrastructure/port'];
  const sqlserverDatabase = annotations['sqlserver.infrastructure/database'];
  const sqlserverUser = annotations['sqlserver.infrastructure/user'];
  const sqlserverPassword = annotations['sqlserver.infrastructure/password'];
  const sqlserverVersion = annotations['sqlserver.infrastructure/version'];
  const cdcEnabled = annotations['sqlserver.infrastructure/cdc-enabled'];
  const sqlserverAgent = annotations['sqlserver.infrastructure/agent-status'];
  const sshCommand = annotations['sqlserver.infrastructure/ssh-command'];
  const sshPrivateKeyEncoded = annotations['sqlserver.infrastructure/ssh-private-key'];

  if (templateType !== 'aws-sqlserver-cdc-source' || !sqlserverHost) return null;

  const sshPrivateKey = useMemo(() => {
    if (!sshPrivateKeyEncoded) return '';
    try {
      return window.atob(sshPrivateKeyEncoded);
    } catch (e) {
      console.error('Failed to decode SSH key:', e);
      return '';
    }
  }, [sshPrivateKeyEncoded]);

  const downloadSSHKey = () => {
    const element = document.createElement('a');
    const file = new Blob([sshPrivateKey], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'sqlserver_key.pem';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const host = sqlserverHost;
  const port = sqlserverPort || '1433';
  const database = sqlserverDatabase || 'rdi_source_db';
  const user = sqlserverUser || 'rdi_user';

  const rdiConfig = `connection:
  type: sqlserver
  host: ${host}
  port: ${port}
  database: ${database}
  user: ${user}
  password: ${sqlserverPassword || 'RedisCDC2024!'}
advanced:
  source:
    connector.class: io.debezium.connector.sqlserver.SqlServerConnector
    snapshot.mode: initial`;

  return (
    <InfoCard title="SQL Server CDC Source Information" icon={<RiDatabase2Line size={20} />}>
      <InfraStack>
        <InfraSection title="🔷 SQL Server Configuration">
          {sqlserverVersion && (
            <InfraField label="Version:" value={<InfraTagValue>{`SQL Server ${sqlserverVersion}`}</InfraTagValue>} mono={false} />
          )}
          {cdcEnabled === 'true' && (
            <InfraField label="CDC Status:" value={<InfraTagRow><InfraTag><RiCheckLine size={14} style={{ marginRight: 4 }} />Enabled</InfraTag></InfraTagRow>} mono={false} />
          )}
          {sqlserverAgent && (
            <InfraField label="SQL Server Agent:" value={<InfraTagValue>{sqlserverAgent}</InfraTagValue>} mono={false} />
          )}
        </InfraSection>

        <InfraSection title="🔌 Connection Information">
          <CopyableValue label="Host:" value={host} />
          <InfraField label="Port:" value={port} />
          <InfraField label="Database:" value={database} />
          <InfraField label="User:" value={user} />
          {sqlserverPassword && <CopyableValue label="Password:" value={sqlserverPassword} />}
        </InfraSection>

        <InfraDivider />
        <InfraSection title="🚀 RDI Configuration">
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock variant="light">{rdiConfig}</InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={rdiConfig} />
            </div>
          </div>
        </InfraSection>

        {sshPrivateKey && (
          <>
            <InfraDivider />
            <InfraSection title="🔐 SSH Access">
              {sshCommand && (
                <>
                  <InfraField label="SSH Command:" value="" mono={false} />
                  <div style={{ position: 'relative' }}>
                    <InfraCodeBlock variant="light">{sshCommand}</InfraCodeBlock>
                    <div style={{ position: 'absolute', top: 4, right: 4 }}>
                      <CopyButton text={sshCommand} />
                    </div>
                  </div>
                </>
              )}
              <InfraField label="SSH Private Key:" value="" mono={false} />
              <Flex gap="2">
                <Button variant="primary" size="small" onPress={downloadSSHKey}>
                  <RiDownloadLine size={16} />
                  Download Key
                </Button>
                <Button
                  variant="secondary"
                  size="small"
                  onPress={() => navigator.clipboard.writeText(sshPrivateKey)}
                >
                  <RiFileCopyLine size={16} />
                  Copy Key
                </Button>
              </Flex>
              <Text variant="body-small" style={{ marginTop: '0.5rem', display: 'block' }}>
                💡 Save as sqlserver_key.pem and run: chmod 400 sqlserver_key.pem
              </Text>
            </InfraSection>
          </>
        )}
      </InfraStack>
    </InfoCard>
  );
};
